import { heading, isBlockEmpty, moveToNextLeafBlock, updates } from '@gitbook/doc-core';
import { type Change, isBlockRangeSelection } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import { isBackspace, isDelete, isEnter, isModEnter } from '../common';

export const plugin: Plugin = {
    onKeyDown,
};

/**
 * Handle key down events for update blocks.
 */
function onKeyDown(event: KeyboardEvent, change: Change): Change | undefined {
    if (isBlockRangeSelection(change.value.selection)) {
        return;
    }

    if (isModEnter(event)) {
        return onModEnter(event, change);
    }

    if (isEnter(event)) {
        return onEnter(event, change);
    }

    if (isBackspace(event) || isDelete(event)) {
        return onDelete(event, change);
    }

    return undefined;
}

/**
 * Pressing enter in an update block. Contains override to focus existing paragraph instead of inserting a new paragraph below.
 */
function onEnter(event: KeyboardEvent, change: Change): Change | undefined {
    const { value } = change;

    if (!value.isCollapsed) {
        return undefined;
    }

    const { startBlock } = value;
    if (!startBlock || !heading.isHeading(startBlock)) {
        return undefined;
    }

    const current = updates.getCurrentUpdate(value);
    if (!current) {
        return undefined;
    }

    const firstBlock = current.update.blockNodes.first();
    if (!firstBlock || !heading.isHeading(firstBlock) || !value.selection) {
        return undefined;
    }

    const isFirstHeading = firstBlock.key === startBlock.key;
    const isAtEndOfFirstHeading = isFirstHeading
        ? value.selection.hasFocusAtEndOf(firstBlock)
        : false;

    // Instead of inserting a new paragraph below (default paragraph-plugin behaviour), we instead focus the next paragraph as we add one by default.
    if (isAtEndOfFirstHeading) {
        event.preventDefault();
        moveToNextLeafBlock(change);

        return change;
    }

    return undefined;
}

/**
 * Pressing backspace or delete in a update block.
 */
function onDelete(_event: KeyboardEvent, change: Change): Change | undefined {
    const current = updates.getCurrentUpdate(change.value);

    if (!current) {
        return undefined;
    }

    const firstBlock = current.update.blockNodes.first();

    // There is only one empty node.
    if (
        current.update.nodes.size === 1 &&
        firstBlock &&
        isBlockEmpty(firstBlock) &&
        change.value.selection.hasAnchorAtStartOf(firstBlock)
    ) {
        change.removeNodeByKey(current.update.key);
        return change;
    }

    return undefined;
}

/**
 * Pressing mod+enter, exit the whole update block.
 */
function onModEnter(event: KeyboardEvent, change: Change): Change | undefined {
    const { value } = change;

    if (!value.isCollapsed) {
        return;
    }

    const current = updates.getCurrentUpdate(change.value);
    if (!current) {
        return;
    }

    event.preventDefault();

    moveToNextLeafBlock(change, current.updates);
    return change;
}
